import { useState, useEffect, useMemo, useRef } from 'react';
import {
  PenTool,
  Plus,
  Minus,
  Edit2,
  Trash2,
  Loader2,
  Save,
  User,
  AlertCircle,
  Search,
  BarChart3,
  Flame,
  ChevronDown,
  CheckCircle2,
  Target,
  Square,
} from 'lucide-react';
import {
  fetchHanziWords,
  createHanziWord,
  updateHanziWord,
  deleteHanziWord,
  fetchStudentProgress,
  fetchAllStudentCharacterProgress,
  HanziWord,
  HanziCharacterProgress,
  getHanziConfig,
  HanziConfig,
  parseExampleSentencesFromTextarea,
  serializeExampleSentencesForTextarea,
  resolveMoeLevels,
  resolveMoeTypes,
  DEFAULT_REMEDIAL_STEPS,
  normalizeRemedialSteps,
  cycleHanziRemedialStepType,
  type HanziRemedialStepType,
} from '../../../services/hanziHeroService';
import PrizeWheel, { PRIZE_WHEEL_SEGMENT_COLORS } from '../../hanzi-hero/PrizeWheel';
import { fetchTuteeComponents, updateTuteeComponentConfig } from '../../../services/componentService';
import { fetchStudentsForTutee } from '../../../services/tuteeService';
import { Tutee } from '../../../types/tuition';
import ConfirmationModal from '../../../components/ui/ConfirmationModal';
import AnimatedCard from '../../../components/ui/AnimatedCard';
import { TUTEE_ICON_MAP } from '../../../utils/tuteeIcons';
import HanziGridStylePreview from './HanziGridStylePreview';

const iconMap: Record<string, React.ComponentType<{ className?: string }>> = TUTEE_ICON_MAP;

interface HanziHeroConfigProps {
  tutees: Tutee[];
  selectedTuteeId: string;
}

const DEFAULT_PRIZE_SEGMENTS = [
  { label: 'Star', weight: 1 },
  { label: 'Heart', weight: 1 },
  { label: 'Trophy', weight: 1 },
];

/** Shared height, border, and focus ring for selects and numeric fields in this panel */
const HANZI_FORM_CONTROL =
  'w-full min-h-[2.75rem] px-3 py-2.5 text-sm text-gray-800 bg-white border border-gray-200 rounded-xl shadow-sm transition-[color,box-shadow,border-color] focus:outline-none focus:ring-2 focus:ring-amber-500/25 focus:border-amber-400';

const HANZI_NUMBER_CONTROL = `${HANZI_FORM_CONTROL} [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none`;

const MOE_LEVEL_OPTIONS = ['P1', 'P2', 'P3', 'P4', 'P5', 'P6'] as const;

function remedialTypeAdminTitle(t: HanziRemedialStepType): string {
  if (t === 'writer_guided') return 'Stroke guide';
  if (t === 'grid_with_model') return 'Grid + 范字';
  return 'Blank grid';
}

const HanziHeroConfig = ({ tutees, selectedTuteeId }: HanziHeroConfigProps) => {
  const [selectedStudent, setSelectedStudent] = useState<string>('');
  const [words, setWords] = useState<HanziWord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [batchInput, setBatchInput] = useState('');
  const [isBatchAdding, setIsBatchAdding] = useState(false);
  const [editingWord, setEditingWord] = useState<HanziWord | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{ isOpen: boolean; wordId: string | null }>({
    isOpen: false,
    wordId: null,
  });
  const [isDeleting, setIsDeleting] = useState(false);
  const [newWord, setNewWord] = useState({
    character: '',
    pinyin: '',
    notes: '',
    exampleLinesText: '',
  });
  const [config, setConfig] = useState<HanziConfig>(getHanziConfig());
  const [hanziTuteeCompId, setHanziTuteeCompId] = useState<string | null>(null);
  const [isSavingConfig, setIsSavingConfig] = useState(false);
  const [loadedStudents, setLoadedStudents] = useState<{ value: string; label: string }[]>([]);
  const [studentProgress, setStudentProgress] = useState<{
    attemptCount: number;
    correctCount: number;
    highestStreak: number;
    currentStreak: number;
  } | null>(null);
  const [characterPracticeRows, setCharacterPracticeRows] = useState<HanziCharacterProgress[]>([]);
  const [testSpinLabel, setTestSpinLabel] = useState<string | null>(null);

  useEffect(() => {
    const loadStudents = async () => {
      if (!selectedTuteeId) return;
      try {
        const students = await fetchStudentsForTutee(selectedTuteeId);
        const options = students.map((s) => ({
          value: s.studentName.toLowerCase(),
          label: s.studentName.charAt(0).toUpperCase() + s.studentName.slice(1),
        }));
        setLoadedStudents(options);
      } catch (err) {
        console.error(err);
        setLoadedStudents([]);
      }
    };
    loadStudents();
  }, [selectedTuteeId]);

  useEffect(() => {
    if (loadedStudents.length > 0 && (!selectedStudent || !loadedStudents.some((o) => o.value === selectedStudent))) {
      setSelectedStudent(loadedStudents[0].value);
    } else if (loadedStudents.length === 0) {
      setSelectedStudent('');
    }
  }, [loadedStudents, selectedStudent]);

  useEffect(() => {
    const load = async () => {
      if (!selectedTuteeId) return;
      try {
        setLoading(true);
        setError('');
        const [wordList, tuteeComps, progress, charRows] = await Promise.all([
          fetchHanziWords(selectedTuteeId, selectedStudent || undefined, false),
          fetchTuteeComponents(selectedTuteeId),
          fetchStudentProgress(selectedTuteeId, selectedStudent || ''),
          selectedStudent
            ? fetchAllStudentCharacterProgress(selectedTuteeId, selectedStudent)
            : Promise.resolve([] as HanziCharacterProgress[]),
        ]);
        setWords(wordList);
        setCharacterPracticeRows(charRows);
        setStudentProgress(
          progress
            ? {
                attemptCount: progress.attemptCount,
                correctCount: progress.correctCount,
                highestStreak: progress.highestStreak,
                currentStreak: progress.currentStreak,
              }
            : null
        );

        const hanziComp = tuteeComps.find((tc) => tc.component?.componentType === 'hanzi_hero');
        if (hanziComp) {
          setHanziTuteeCompId(hanziComp.id);
          setConfig(getHanziConfig(hanziComp.config));
        } else {
          setHanziTuteeCompId(null);
          setConfig(getHanziConfig());
        }
      } catch (err) {
        console.error(err);
        setError('Failed to load data');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [selectedTuteeId, selectedStudent]);

  const loadWords = async () => {
    if (!selectedTuteeId) return;
    try {
      const list = await fetchHanziWords(selectedTuteeId, selectedStudent || undefined, false);
      setWords(list);
    } catch (err) {
      console.error(err);
    }
  };

  const selectedTutee = useMemo(() => tutees.find((t) => t.id === selectedTuteeId), [tutees, selectedTuteeId]);

  const getIcon = (iconName: string) => iconMap[iconName] || User;

  const handleAddWord = async () => {
    if (!newWord.character.trim() || !selectedTuteeId) return;
    try {
      setError('');
      await createHanziWord({
        tuteeId: selectedTuteeId,
        studentName: selectedStudent || undefined,
        character: newWord.character.trim(),
        pinyin: newWord.pinyin.trim() || undefined,
        notes: newWord.notes.trim() || undefined,
        exampleSentences: parseExampleSentencesFromTextarea(newWord.exampleLinesText),
        displayOrder: words.length,
      });
      setNewWord({ character: '', pinyin: '', notes: '', exampleLinesText: '' });
      setShowAddModal(false);
      await loadWords();
    } catch (err) {
      console.error(err);
      setError('Failed to add character');
    }
  };

  const handleUpdateWord = async () => {
    if (!editingWord) return;
    try {
      setError('');
      await updateHanziWord(editingWord.id, {
        character: newWord.character.trim(),
        pinyin: newWord.pinyin.trim() || undefined,
        notes: newWord.notes.trim() || undefined,
        exampleSentences: parseExampleSentencesFromTextarea(newWord.exampleLinesText),
      });
      setEditingWord(null);
      setNewWord({ character: '', pinyin: '', notes: '', exampleLinesText: '' });
      await loadWords();
    } catch (err) {
      console.error(err);
      setError('Failed to update character');
    }
  };

  const handleBatchAdd = async () => {
    if (!batchInput.trim() || !selectedTuteeId) return;
    const chars = batchInput
      .split(/[\s,，、]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (chars.length === 0) return;
    try {
      setIsBatchAdding(true);
      setError('');
      for (let i = 0; i < chars.length; i++) {
        await createHanziWord({
          tuteeId: selectedTuteeId,
          studentName: selectedStudent || undefined,
          character: chars[i],
          displayOrder: words.length + i,
        });
      }
      setBatchInput('');
      setShowBatchModal(false);
      await loadWords();
    } catch (err) {
      console.error(err);
      setError('Failed to add characters');
    } finally {
      setIsBatchAdding(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm.wordId) return;
    try {
      setIsDeleting(true);
      await deleteHanziWord(deleteConfirm.wordId);
      setDeleteConfirm({ isOpen: false, wordId: null });
      await loadWords();
    } catch (err) {
      console.error(err);
      setError('Failed to delete');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleSaveConfig = async () => {
    if (!hanziTuteeCompId) return;
    try {
      setIsSavingConfig(true);
      const rawSegs = config.prizeWheelSegments?.length ? config.prizeWheelSegments : DEFAULT_PRIZE_SEGMENTS;
      const prizeWheelSegments = rawSegs
        .map((s) => ({
          label: (s.label ?? '').trim() || 'Prize',
          weight: Math.max(1, Math.min(100, Math.floor(Number(s.weight) || 1))),
        }))
        .filter((s) => s.label.length > 0);
      const normalizedSegments =
        prizeWheelSegments.length > 0 ? prizeWheelSegments : [{ label: 'Star', weight: 1 }];
      const prizeWheelMinStreak = Math.max(1, Math.min(365, Math.floor(config.prizeWheelMinStreak ?? 1)));
      const charactersPerSession = Math.max(1, Math.min(20, Math.floor(config.charactersPerSession ?? 5)));
      const moeLevels = resolveMoeLevels(config);
      const moeTypes = resolveMoeTypes(config);
      const writerDelayBetweenStrokesMs = Math.max(
        0,
        Math.min(8000, Math.floor(Number(config.writerDelayBetweenStrokesMs) || 1000))
      );
      const remedialStepsByStudent = config.remedialStepsByStudent
        ? Object.fromEntries(
            Object.entries(config.remedialStepsByStudent).map(([k, v]) => [
              k.toLowerCase().trim(),
              normalizeRemedialSteps(v),
            ])
          )
        : undefined;
      const payload: HanziConfig = {
        ...config,
        moeLevels,
        moeLevel: moeLevels[0] ?? 'P5',
        moeTypes,
        moeType: moeTypes[0] ?? 'write',
        prizeWheelSegments: normalizedSegments,
        prizeWheelMinStreak,
        charactersPerSession,
        writerDelayBetweenStrokesMs,
        writerQuizShowOutline: config.writerQuizShowOutline !== false,
        remedialStepsByStudent:
          remedialStepsByStudent && Object.keys(remedialStepsByStudent).length > 0
            ? remedialStepsByStudent
            : undefined,
      };
      await updateTuteeComponentConfig(hanziTuteeCompId, payload as Record<string, unknown>);
      setConfig(payload);
    } catch (err) {
      console.error(err);
      setError('Failed to save settings');
    } finally {
      setIsSavingConfig(false);
    }
  };

  const prizeSegments = config.prizeWheelSegments?.length ? config.prizeWheelSegments : DEFAULT_PRIZE_SEGMENTS;

  const updatePrizeSegment = (index: number, patch: Partial<{ label: string; weight: number }>) => {
    setConfig((c) => {
      const segs = [...(c.prizeWheelSegments?.length ? c.prizeWheelSegments : DEFAULT_PRIZE_SEGMENTS)];
      segs[index] = { ...segs[index], ...patch };
      return { ...c, prizeWheelSegments: segs };
    });
  };

  const addPrizeSegment = () => {
    setConfig((c) => {
      const segs = c.prizeWheelSegments?.length ? c.prizeWheelSegments : DEFAULT_PRIZE_SEGMENTS;
      return { ...c, prizeWheelSegments: [...segs, { label: 'Prize', weight: 1 }] };
    });
  };

  const removePrizeSegment = (index: number) => {
    setConfig((c) => {
      const segs = [...(c.prizeWheelSegments?.length ? c.prizeWheelSegments : DEFAULT_PRIZE_SEGMENTS)];
      if (segs.length <= 1) return c;
      segs.splice(index, 1);
      return { ...c, prizeWheelSegments: segs };
    });
  };

  const selectedMoeLevels = resolveMoeLevels(config);
  const selectedMoeTypes = resolveMoeTypes(config);

  const remedialPlanForStudent = useMemo(() => {
    const k = selectedStudent || '';
    const raw = config.remedialStepsByStudent?.[k];
    return raw?.length ? normalizeRemedialSteps(raw) : [...DEFAULT_REMEDIAL_STEPS];
  }, [config.remedialStepsByStudent, selectedStudent]);

  const setRemedialPlanForStudentKey = (next: HanziRemedialStepType[]) => {
    const k = selectedStudent || '';
    setConfig((c) => ({
      ...c,
      remedialStepsByStudent: {
        ...(c.remedialStepsByStudent ?? {}),
        [k]: next,
      },
    }));
  };

  const hasMoeCharSource = config.characterSource === 'moe' || config.characterSource === 'both';
  const hasCustomCharSource = config.characterSource === 'custom' || config.characterSource === 'both';

  const toggleCharSourceMoe = () => {
    setConfig((c) => {
      const moe = c.characterSource === 'moe' || c.characterSource === 'both';
      const custom = c.characterSource === 'custom' || c.characterSource === 'both';
      if (moe && !custom) return c;
      if (moe && custom) return { ...c, characterSource: 'custom' };
      return { ...c, characterSource: 'both' };
    });
  };

  const toggleCharSourceCustom = () => {
    setConfig((c) => {
      const moe = c.characterSource === 'moe' || c.characterSource === 'both';
      const custom = c.characterSource === 'custom' || c.characterSource === 'both';
      if (custom && !moe) return c;
      if (moe && custom) return { ...c, characterSource: 'moe' };
      return { ...c, characterSource: 'both' };
    });
  };

  const toggleMoeType = (t: 'read' | 'write') => {
    setConfig((c) => {
      const cur = resolveMoeTypes(c);
      let next: ('read' | 'write')[];
      if (cur.includes(t)) {
        next = cur.filter((x) => x !== t);
        if (next.length === 0) return c;
      } else {
        const rank: Record<'read' | 'write', number> = { write: 0, read: 1 };
        next = [...cur, t].sort((a, b) => rank[a] - rank[b]);
      }
      return { ...c, moeTypes: next, moeType: next[0] };
    });
  };

  const toggleMoeLevel = (level: string) => {
    setConfig((c) => {
      const cur = resolveMoeLevels(c);
      let next: string[];
      if (cur.includes(level)) {
        next = cur.filter((l) => l !== level);
        if (next.length === 0) return c;
      } else {
        next = [...cur, level].sort((a, b) => parseInt(a.slice(1), 10) - parseInt(b.slice(1), 10));
      }
      return { ...c, moeLevels: next, moeLevel: next[0] };
    });
  };

  const pinyinByCharacter = useMemo(() => {
    const m = new Map<string, string>();
    for (const w of words) {
      const py = w.pinyin?.trim();
      if (py) m.set(w.character, py);
    }
    return m;
  }, [words]);

  const { practicePerfect, practiceStruggling } = useMemo(() => {
    const rows = [...characterPracticeRows].sort((a, b) => a.character.localeCompare(b.character, 'zh-Hans'));
    const perfect = rows.filter((r) => r.attemptCount > 0 && r.correctCount === r.attemptCount);
    const struggling = rows.filter((r) => r.attemptCount > 0 && r.correctCount < r.attemptCount);
    return { practicePerfect: perfect, practiceStruggling: struggling };
  }, [characterPracticeRows]);

  const practiceOverview = useMemo(() => {
    if (!selectedStudent) return null;
    const p = studentProgress ?? {
      attemptCount: 0,
      correctCount: 0,
      highestStreak: 0,
      currentStreak: 0,
    };
    let attemptsSum = 0;
    let correctSum = 0;
    for (const r of characterPracticeRows) {
      attemptsSum += r.attemptCount;
      correctSum += r.correctCount;
    }
    const characterAccuracyPct =
      attemptsSum > 0 ? Math.min(100, Math.round((correctSum / attemptsSum) * 100)) : null;
    return { p, characterAccuracyPct };
  }, [selectedStudent, studentProgress, characterPracticeRows]);

  const wheelPreviewSegments = useMemo(() => {
    const raw = prizeSegments.map((s) => ({
      label: (s.label ?? '').trim() || 'Prize',
      weight: Math.max(1, Math.min(100, Math.floor(Number(s.weight) || 1))),
    }));
    const filtered = raw.filter((s) => s.label.length > 0);
    return filtered.length > 0 ? filtered : [{ label: 'Prize', weight: 1 }];
  }, [prizeSegments]);

  const wheelShapeKey = `${wheelPreviewSegments.length}-${wheelPreviewSegments.map((s) => s.weight).join('-')}`;
  const prevWheelShapeKey = useRef(wheelShapeKey);
  useEffect(() => {
    if (prevWheelShapeKey.current !== wheelShapeKey) {
      prevWheelShapeKey.current = wheelShapeKey;
      setTestSpinLabel(null);
    }
  }, [wheelShapeKey]);

  return (
    <AnimatedCard className="overflow-hidden">
      <div id="hanzi-hero-admin-section" className="p-6 md:p-8">
        <div className="flex gap-2 sm:gap-3 mb-8 w-full">
            <button
              onClick={() => {
                setBatchInput('');
                setShowBatchModal(true);
              }}
              className="flex-1 px-4 py-2.5 bg-amber-50 text-amber-700 rounded-xl hover:bg-amber-100 font-bold text-sm border border-amber-100"
            >
              Batch Add
            </button>
            <button
              onClick={() => {
                setEditingWord(null);
                setNewWord({ character: '', pinyin: '', notes: '', exampleLinesText: '' });
                setShowAddModal(true);
              }}
              className="flex-1 px-4 py-2.5 bg-amber-600 text-white rounded-xl hover:bg-amber-700 font-bold text-sm"
            >
              <Plus className="w-4 h-4 inline mr-1" />
              Add Character
            </button>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-100 rounded-2xl flex items-center gap-3 text-red-600">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <p className="text-sm font-medium">{error}</p>
          </div>
        )}

        <div className="mb-8 p-6 bg-gray-50 rounded-3xl border border-gray-100">
          {loadedStudents.length > 0 && (
            <div>
              <label className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5 block">Student</label>
              {loadedStudents.length <= 4 ? (
                <div className="flex flex-wrap gap-2">
                  {loadedStudents.map((s) => {
                    const active = selectedStudent === s.value;
                    const grad =
                      selectedTutee?.colorScheme.gradient ?? 'from-amber-500 to-orange-500';
                    return (
                      <button
                        key={s.value}
                        type="button"
                        onClick={() => setSelectedStudent(s.value)}
                        className={`px-4 py-2.5 rounded-xl text-sm font-bold border transition-all ${
                          active
                            ? `bg-gradient-to-r ${grad} text-white border-transparent shadow-md`
                            : 'bg-white text-gray-600 border-gray-200 hover:border-amber-200 hover:bg-amber-50/40'
                        }`}
                      >
                        {s.label}
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div className="relative max-w-xs">
                  <select
                    value={selectedStudent}
                    onChange={(e) => setSelectedStudent(e.target.value)}
                    className={`${HANZI_FORM_CONTROL} appearance-none cursor-pointer pr-10`}
                  >
                    {loadedStudents.map((s) => (
                      <option key={s.value} value={s.value}>
                        {s.label}
                      </option>
                    ))}
                  </select>
                  <ChevronDown
                    className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500"
                    aria-hidden
                  />
                </div>
              )}
            </div>
          )}

          {practiceOverview && (
            <div className="mt-5 space-y-5">
              <div className="rounded-2xl border border-amber-100 bg-white p-5 shadow-sm">
                <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                  <div className="flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-amber-600" />
                    <h3 className="text-base font-bold text-gray-800">Student practice stats</h3>
                  </div>
                  <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Selected student</span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div className="rounded-xl bg-amber-50/80 border border-amber-100/80 px-3 py-3">
                    <p className="text-[11px] font-bold text-amber-800/80 uppercase tracking-wide">Sessions</p>
                    <p className="text-2xl font-black text-amber-950 tabular-nums">{practiceOverview.p.attemptCount}</p>
                  </div>
                  <div className="rounded-xl bg-amber-50/80 border border-amber-100/80 px-3 py-3">
                    <p className="text-[11px] font-bold text-amber-800/80 uppercase tracking-wide">Chars correct</p>
                    <p className="text-2xl font-black text-amber-950 tabular-nums">{practiceOverview.p.correctCount}</p>
                  </div>
                  <div className="rounded-xl bg-orange-50/80 border border-orange-100/80 px-3 py-3">
                    <p className="text-[11px] font-bold text-orange-800/80 uppercase tracking-wide flex items-center gap-1">
                      <Flame className="w-3 h-3" /> Streak
                    </p>
                    <p className="text-2xl font-black text-orange-950 tabular-nums">{practiceOverview.p.currentStreak}</p>
                    <p className="text-[10px] text-orange-800/70 mt-0.5">Best {practiceOverview.p.highestStreak}</p>
                  </div>
                  <div className="rounded-xl bg-gray-50 border border-gray-100 px-3 py-3">
                    <p className="text-[11px] font-bold text-gray-500 uppercase tracking-wide">Character accuracy</p>
                    <p className="text-2xl font-black text-gray-800 tabular-nums">
                      {practiceOverview.characterAccuracyPct !== null
                        ? `${practiceOverview.characterAccuracyPct}%`
                        : '—'}
                    </p>
                  </div>
                </div>
              </div>

              {characterPracticeRows.length > 0 ? (
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="rounded-2xl border border-emerald-200/90 bg-gradient-to-b from-emerald-50/90 to-white p-5 shadow-sm">
                    <div className="flex items-center gap-2 mb-1">
                      <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
                      <h4 className="font-bold text-emerald-950">Always correct</h4>
                      <span className="ml-auto text-xs font-bold tabular-nums text-emerald-700 bg-emerald-100/80 px-2 py-0.5 rounded-md">
                        {practicePerfect.length}
                      </span>
                    </div>
                    <p className="text-xs text-emerald-800/75 mb-4 leading-relaxed">
                      Every recorded attempt for these characters was marked correct (quiz / self-check).
                    </p>
                    <div className="flex flex-wrap gap-2 min-h-[2.5rem]">
                      {practicePerfect.length > 0 ? (
                        practicePerfect.map((r) => {
                          const py = pinyinByCharacter.get(r.character);
                          return (
                            <div
                              key={r.character}
                              className="flex flex-col items-center gap-0.5 min-w-[3.25rem] px-2.5 py-2 bg-white rounded-xl border border-emerald-200/70 shadow-sm"
                            >
                              <span
                                className="text-2xl font-serif leading-none text-gray-900"
                                style={{ fontFamily: 'KaTi Maobi, KaiTi, serif' }}
                              >
                                {r.character}
                              </span>
                              {py && <span className="text-[10px] text-gray-500 leading-tight">{py}</span>}
                              <span className="text-[10px] font-semibold text-emerald-700 tabular-nums">
                                {r.correctCount}/{r.attemptCount}
                              </span>
                            </div>
                          );
                        })
                      ) : (
                        <p className="text-xs text-emerald-800/60 py-1">None yet — keep practicing.</p>
                      )}
                    </div>
                  </div>

                  <div className="rounded-2xl border border-amber-200/90 bg-gradient-to-b from-amber-50/80 to-white p-5 shadow-sm">
                    <div className="flex items-center gap-2 mb-1">
                      <Target className="w-5 h-5 text-amber-700 shrink-0" />
                      <h4 className="font-bold text-amber-950">Needs more practice</h4>
                      <span className="ml-auto text-xs font-bold tabular-nums text-amber-800 bg-amber-100/90 px-2 py-0.5 rounded-md">
                        {practiceStruggling.length}
                      </span>
                    </div>
                    <p className="text-xs text-amber-900/75 mb-4 leading-relaxed">
                      At least one attempt was incorrect. Review these characters in the next session.
                    </p>
                    <div className="flex flex-wrap gap-2 min-h-[2.5rem]">
                      {practiceStruggling.length > 0 ? (
                        practiceStruggling.map((r) => {
                          const py = pinyinByCharacter.get(r.character);
                          const wrong = r.attemptCount - r.correctCount;
                          return (
                            <div
                              key={r.character}
                              className="flex flex-col items-center gap-0.5 min-w-[3.25rem] px-2.5 py-2 bg-white rounded-xl border border-amber-200/80 shadow-sm"
                            >
                              <span
                                className="text-2xl font-serif leading-none text-gray-900"
                                style={{ fontFamily: 'KaTi Maobi, KaiTi, serif' }}
                              >
                                {r.character}
                              </span>
                              {py && <span className="text-[10px] text-gray-500 leading-tight">{py}</span>}
                              <span className="text-[10px] font-semibold text-amber-800 tabular-nums">
                                {r.correctCount}/{r.attemptCount} ok
                              </span>
                              <span className="text-[10px] text-red-600/90 font-medium tabular-nums">
                                {wrong} miss{wrong !== 1 ? 'es' : ''}
                              </span>
                            </div>
                          );
                        })
                      ) : (
                        <p className="text-xs text-amber-900/60 py-1">
                          None — no incorrect attempts logged for practiced characters.
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50/50 px-4 py-6 text-center">
                  <p className="text-sm text-gray-600">
                    No per-character history yet for this student. Stats fill in as they use Hanzi Hero.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {hanziTuteeCompId && (
          <div className="mb-8 p-6 bg-amber-50/50 rounded-2xl border border-amber-100 space-y-8">
            <div>
              <h3 className="font-bold text-gray-800 mb-1">Session & Prize Settings</h3>
              <p className="text-sm text-gray-600 mb-5">
                Session controls what students practice; prize wheel rules and slices are below.
              </p>

              <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Session</p>
              <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-x-4 gap-y-4">
                <div className="sm:col-span-2 xl:col-span-4">
                  <span className="text-xs font-bold text-gray-500 block mb-1.5">Character source</span>
                  <p className="text-xs text-gray-500 mb-2.5">
                    Turn on MOE lists, your custom word bank, or both. At least one source must stay on.
                  </p>
                  <div className="flex flex-wrap gap-2" role="group" aria-label="Practice character sources">
                    <button
                      type="button"
                      onClick={toggleCharSourceMoe}
                      className={`px-4 py-2 rounded-xl text-sm font-bold border transition-colors ${
                        hasMoeCharSource
                          ? 'bg-amber-600 text-white border-amber-600 shadow-sm'
                          : 'bg-white text-gray-600 border-gray-200 hover:border-amber-300 hover:bg-amber-50/50'
                      }`}
                    >
                      MOE curriculum
                    </button>
                    <button
                      type="button"
                      onClick={toggleCharSourceCustom}
                      className={`px-4 py-2 rounded-xl text-sm font-bold border transition-colors ${
                        hasCustomCharSource
                          ? 'bg-amber-600 text-white border-amber-600 shadow-sm'
                          : 'bg-white text-gray-600 border-gray-200 hover:border-amber-300 hover:bg-amber-50/50'
                      }`}
                    >
                      Custom word bank
                    </button>
                  </div>
                </div>
                <div className="sm:col-span-2 xl:col-span-4">
                  <span className="text-xs font-bold text-gray-500 block mb-1.5">MOE levels</span>
                  <p className="text-xs text-gray-500 mb-2.5">
                    Include curriculum characters from every selected level (e.g. P1–P5). At least one level
                    must stay on.
                  </p>
                  <div className="flex flex-wrap gap-2" role="group" aria-label="MOE primary levels">
                    {MOE_LEVEL_OPTIONS.map((lvl) => {
                      const on = selectedMoeLevels.includes(lvl);
                      return (
                        <button
                          key={lvl}
                          type="button"
                          onClick={() => toggleMoeLevel(lvl)}
                          className={`min-w-[2.75rem] px-3 py-2 rounded-xl text-sm font-bold border transition-colors ${
                            on
                              ? 'bg-amber-600 text-white border-amber-600 shadow-sm'
                              : 'bg-white text-gray-600 border-gray-200 hover:border-amber-300 hover:bg-amber-50/50'
                          }`}
                        >
                          {lvl}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div className="sm:col-span-2 xl:col-span-2">
                  <span className="text-xs font-bold text-gray-500 block mb-1.5">MOE type</span>
                  <p className="text-xs text-gray-500 mb-2.5">
                    Include 识写 (write) and/or 识读 (read) lists. At least one stays selected.
                  </p>
                  <div className="flex flex-wrap gap-2" role="group" aria-label="MOE curriculum type">
                    {(
                      [
                        { id: 'write' as const, label: '识写 Write' },
                        { id: 'read' as const, label: '识读 Read' },
                      ]
                    ).map(({ id, label }) => {
                      const on = selectedMoeTypes.includes(id);
                      return (
                        <button
                          key={id}
                          type="button"
                          onClick={() => toggleMoeType(id)}
                          className={`px-4 py-2 rounded-xl text-sm font-bold border transition-colors ${
                            on
                              ? 'bg-amber-600 text-white border-amber-600 shadow-sm'
                              : 'bg-white text-gray-600 border-gray-200 hover:border-amber-300 hover:bg-amber-50/50'
                          }`}
                        >
                          {label}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div>
                  <label className="text-xs font-bold text-gray-500 block mb-1.5">Characters per session</label>
                  <input
                    type="number"
                    min={1}
                    max={20}
                    value={config.charactersPerSession ?? 5}
                    onChange={(e) =>
                      setConfig((c) => ({ ...c, charactersPerSession: parseInt(e.target.value, 10) || 5 }))
                    }
                    className={HANZI_NUMBER_CONTROL}
                  />
                </div>
                <div className="sm:col-span-2 xl:col-span-4">
                  <span className="text-xs font-bold text-gray-500 block mb-1.5">Grid style</span>
                  <div className="flex flex-col sm:flex-row sm:items-end gap-4">
                    <div className="relative flex-1 min-w-0 max-w-md">
                      <label htmlFor="hanzi-grid-style" className="sr-only">
                        Grid style
                      </label>
                      <select
                        id="hanzi-grid-style"
                        value={config.gridStyle ?? 'tianzige'}
                        onChange={(e) =>
                          setConfig((c) => ({
                            ...c,
                            gridStyle: e.target.value as 'jiugong' | 'tianzige' | 'mizige',
                          }))
                        }
                        className={`${HANZI_FORM_CONTROL} appearance-none cursor-pointer pr-10`}
                      >
                        <option value="jiugong">九宫格 (nine-square)</option>
                        <option value="tianzige">田字格 (field grid)</option>
                        <option value="mizige">米字格 (rice grid)</option>
                      </select>
                      <ChevronDown
                        className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500"
                        aria-hidden
                      />
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <span className="text-[11px] font-bold text-gray-400 uppercase tracking-wider hidden sm:inline">
                        Preview
                      </span>
                      <HanziGridStylePreview gridStyle={config.gridStyle ?? 'tianzige'} size={80} />
                    </div>
                  </div>
                </div>

                <div className="sm:col-span-2 xl:col-span-4 border-t border-amber-100/80 pt-5 mt-1">
                  <span className="text-xs font-bold text-gray-500 block mb-1">Model character (范字)</span>
                  <p className="text-sm text-gray-600 mb-1 leading-relaxed">
                    <strong>范字</strong> is the faint sample character shown inside the writing grid while students
                    trace or copy. It helps beginners see shape and proportions; turning it off gives a blank grid for
                    recall practice.
                  </p>
                  <p className="text-xs text-gray-500 mb-3">Uses your current grid style. Pick one:</p>
                  <div className="flex flex-wrap gap-4">
                    <button
                      type="button"
                      onClick={() => setConfig((c) => ({ ...c, showModelCharacter: true }))}
                      className={`flex flex-col items-center gap-2 p-3 rounded-2xl border-2 transition-all max-w-[9.5rem] ${
                        config.showModelCharacter !== false
                          ? 'border-amber-500 bg-amber-50/60 ring-2 ring-amber-400/30'
                          : 'border-gray-200 bg-white hover:border-amber-200'
                      }`}
                    >
                      <HanziGridStylePreview
                        gridStyle={config.gridStyle ?? 'tianzige'}
                        size={88}
                        demoCharacter="学"
                      />
                      <span className="text-xs font-bold text-gray-800 text-center">Show 范字</span>
                      <span className="text-[10px] text-gray-500 text-center leading-tight">Faint guide in grid</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setConfig((c) => ({ ...c, showModelCharacter: false }))}
                      className={`flex flex-col items-center gap-2 p-3 rounded-2xl border-2 transition-all max-w-[9.5rem] ${
                        config.showModelCharacter === false
                          ? 'border-amber-500 bg-amber-50/60 ring-2 ring-amber-400/30'
                          : 'border-gray-200 bg-white hover:border-amber-200'
                      }`}
                    >
                      <HanziGridStylePreview gridStyle={config.gridStyle ?? 'tianzige'} size={88} />
                      <span className="text-xs font-bold text-gray-800 text-center">Blank grid</span>
                      <span className="text-[10px] text-gray-500 text-center leading-tight">No sample character</span>
                    </button>
                  </div>
                </div>

                <div className="sm:col-span-2 xl:col-span-4 border-t border-amber-100/80 pt-5 mt-2">
                  <span className="text-xs font-bold text-gray-500 block mb-1">
                    Wrong-answer practice steps (per student)
                  </span>
                  <p className="text-sm text-gray-600 mb-3 leading-relaxed">
                    After a student taps <strong>No, show me</strong>, they run this sequence before the next
                    character. Choose the student above (same as word list), then set how many steps and tap each
                    box to cycle: <strong>Stroke guide</strong> (HanziWriter) → <strong>Grid + 范字</strong> →{' '}
                    <strong>Blank grid</strong>. Save settings below to apply.
                  </p>
                  <div className="flex flex-wrap items-center gap-2 mb-3">
                    <button
                      type="button"
                      onClick={() =>
                        setRemedialPlanForStudentKey(
                          [...remedialPlanForStudent, 'writer_guided'].slice(0, 12)
                        )
                      }
                      disabled={remedialPlanForStudent.length >= 12}
                      className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-bold border border-gray-200 bg-white hover:bg-amber-50 disabled:opacity-40"
                    >
                      <Plus className="h-4 w-4" aria-hidden />
                      Add step
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        if (remedialPlanForStudent.length <= 1) return;
                        setRemedialPlanForStudentKey(remedialPlanForStudent.slice(0, -1));
                      }}
                      disabled={remedialPlanForStudent.length <= 1}
                      className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-bold border border-gray-200 bg-white hover:bg-amber-50 disabled:opacity-40"
                    >
                      <Minus className="h-4 w-4" aria-hidden />
                      Remove last
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        const k = selectedStudent || '';
                        setConfig((c) => {
                          const next = { ...(c.remedialStepsByStudent ?? {}) };
                          delete next[k];
                          return {
                            ...c,
                            remedialStepsByStudent:
                              Object.keys(next).length > 0 ? next : undefined,
                          };
                        });
                      }}
                      className="text-sm font-semibold text-amber-800 hover:underline"
                    >
                      Use default for this student
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mb-2">
                    Student:{' '}
                    <strong>
                      {loadedStudents.find((s) => s.value === selectedStudent)?.label ||
                        (selectedStudent ? selectedStudent : 'All / unnamed')}
                    </strong>{' '}
                    — tap a box to cycle type
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {remedialPlanForStudent.map((stepType, idx) => (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => {
                          const next = [...remedialPlanForStudent];
                          next[idx] = cycleHanziRemedialStepType(stepType);
                          setRemedialPlanForStudentKey(next);
                        }}
                        className="min-w-[5.5rem] flex flex-col items-center gap-1 rounded-xl border-2 border-amber-200 bg-white px-3 py-3 shadow-sm hover:border-amber-400 hover:bg-amber-50/40 transition-colors"
                      >
                        <span className="text-[10px] font-bold text-gray-400 tabular-nums">Step {idx + 1}</span>
                        {stepType === 'writer_guided' ? (
                          <PenTool className="h-4 w-4 text-amber-700" aria-hidden />
                        ) : stepType === 'grid_with_model' ? (
                          <Target className="h-4 w-4 text-amber-700" aria-hidden />
                        ) : (
                          <Square className="h-4 w-4 text-amber-700" strokeWidth={2.25} aria-hidden />
                        )}
                        <span className="text-xs font-bold text-gray-800 text-center leading-tight">
                          {remedialTypeAdminTitle(stepType)}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="sm:col-span-2 xl:col-span-4 border-t border-amber-100/80 pt-5 mt-2">
                  <span className="text-xs font-bold text-gray-500 block mb-1">Stroke animation timing (Show me)</span>
                  <p className="text-sm text-gray-600 mb-2 leading-relaxed">
                    Pause between each stroke when the full character animates after <strong>Show me</strong>. Higher =
                    slower, easier to follow.
                  </p>
                  <label htmlFor="hanzi-writer-stroke-gap" className="sr-only">
                    Milliseconds between strokes
                  </label>
                  <input
                    id="hanzi-writer-stroke-gap"
                    type="number"
                    min={0}
                    max={8000}
                    step={100}
                    value={config.writerDelayBetweenStrokesMs ?? 1000}
                    onChange={(e) =>
                      setConfig((c) => ({
                        ...c,
                        writerDelayBetweenStrokesMs: Math.min(
                          8000,
                          Math.max(0, parseInt(e.target.value, 10) || 0)
                        ),
                      }))
                    }
                    className={HANZI_NUMBER_CONTROL}
                  />
                  <span className="text-xs text-gray-400 mt-1 block">Milliseconds (0–8000). Default 1000.</span>
                </div>

                <div className="sm:col-span-2 xl:col-span-4">
                  <span className="text-xs font-bold text-gray-500 block mb-1">Trace practice boxes (HanziWriter)</span>
                  <p className="text-sm text-gray-600 mb-3 leading-relaxed">
                    Faint outline under the guided strokes. The app still scores strokes against the correct shape when
                    this is off — students just don’t see the ghost character.
                  </p>
                  <div className="flex flex-wrap gap-3">
                    <button
                      type="button"
                      onClick={() => setConfig((c) => ({ ...c, writerQuizShowOutline: true }))}
                      className={`px-4 py-2 rounded-xl text-sm font-bold border-2 transition-colors ${
                        config.writerQuizShowOutline !== false
                          ? 'border-amber-500 bg-amber-50 text-amber-900'
                          : 'border-gray-200 bg-white text-gray-600 hover:border-amber-200'
                      }`}
                    >
                      Show outline hint
                    </button>
                    <button
                      type="button"
                      onClick={() => setConfig((c) => ({ ...c, writerQuizShowOutline: false }))}
                      className={`px-4 py-2 rounded-xl text-sm font-bold border-2 transition-colors ${
                        config.writerQuizShowOutline === false
                          ? 'border-amber-500 bg-amber-50 text-amber-900'
                          : 'border-gray-200 bg-white text-gray-600 hover:border-amber-200'
                      }`}
                    >
                      No outline (harder)
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="border-t border-amber-200/60 pt-6">
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Prize wheel</p>
              <p className="text-sm text-gray-600 mb-4">
                Streak is counted after today’s check-in. Higher weight means a slice is more likely to win.
              </p>
              <div className="max-w-xs mb-6">
                <label htmlFor="prizeWheelMinStreak" className="text-xs font-bold text-gray-500 block mb-1.5">
                  Min streak to unlock wheel
                </label>
                <input
                  id="prizeWheelMinStreak"
                  type="number"
                  min={1}
                  max={365}
                  value={config.prizeWheelMinStreak ?? 1}
                  onChange={(e) =>
                    setConfig((c) => ({
                      ...c,
                      prizeWheelMinStreak: parseInt(e.target.value, 10) || 1,
                    }))
                  }
                  className={HANZI_NUMBER_CONTROL}
                />
                <p className="text-xs text-gray-500 mt-1.5">Use 1 so every check-in can open the wheel.</p>
              </div>

              <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Wheel slices (label & weight)</p>
              <div className="flex flex-col xl:flex-row xl:items-start gap-8 max-w-5xl">
                <div className="space-y-2 flex-1 min-w-0">
                  {prizeSegments.map((seg, i) => (
                    <div key={i} className="flex flex-wrap items-end gap-2">
                      <div className="flex-1 min-w-[10rem]">
                        <label className="sr-only">Prize label {i + 1}</label>
                        <input
                          type="text"
                          value={seg.label}
                          onChange={(e) => updatePrizeSegment(i, { label: e.target.value })}
                          className={HANZI_FORM_CONTROL}
                          placeholder="Prize name"
                          maxLength={40}
                        />
                      </div>
                      <div className="w-[5.5rem]">
                        <label className="sr-only">Weight for slice {i + 1}</label>
                        <input
                          type="number"
                          min={1}
                          max={100}
                          value={seg.weight}
                          onChange={(e) =>
                            updatePrizeSegment(i, { weight: parseInt(e.target.value, 10) || 1 })
                          }
                          className={HANZI_NUMBER_CONTROL}
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => removePrizeSegment(i)}
                        disabled={prizeSegments.length <= 1}
                        className="shrink-0 p-2.5 rounded-xl border border-gray-200 bg-white text-red-600 hover:bg-red-50 disabled:opacity-40 disabled:pointer-events-none"
                        title="Remove slice"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={addPrizeSegment}
                    className="mt-1 inline-flex items-center gap-2 px-3 py-2 text-sm font-bold text-amber-900 bg-white border border-amber-200 rounded-xl hover:bg-amber-50/80"
                  >
                    <Plus className="w-4 h-4" />
                    Add slice
                  </button>
                </div>

                <div className="shrink-0 w-full max-w-[20rem] mx-auto xl:mx-0 flex flex-col items-stretch gap-3 p-4 rounded-2xl bg-white border border-amber-100/80 shadow-sm">
                  <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">Wheel preview</p>
                  <ul className="text-xs space-y-1.5 border-b border-amber-100/80 pb-3 mb-1">
                    {wheelPreviewSegments.map((s, i) => (
                      <li key={`${i}-${s.label}`} className="flex items-center gap-2 min-w-0">
                        <span
                          className="w-2.5 h-2.5 rounded-full shrink-0 ring-1 ring-black/10"
                          style={{
                            background: PRIZE_WHEEL_SEGMENT_COLORS[i % PRIZE_WHEEL_SEGMENT_COLORS.length],
                          }}
                        />
                        <span className="font-medium text-gray-800 truncate">{s.label}</span>
                        <span className="text-gray-400 ml-auto tabular-nums shrink-0">w{s.weight}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="flex justify-center">
                    <PrizeWheel
                      key={`wh-${wheelPreviewSegments.length}-${wheelPreviewSegments.map((s) => s.weight).join('-')}`}
                      segments={wheelPreviewSegments}
                      confettiOnWin={false}
                      compact
                      singleUse={false}
                      onSpinComplete={(label) => setTestSpinLabel(label)}
                    />
                  </div>
                  {testSpinLabel ? (
                    <p className="text-sm font-bold text-amber-900 text-center bg-amber-50/80 rounded-lg py-2 px-2">
                      Test landed on: {testSpinLabel}
                    </p>
                  ) : (
                    <p className="text-[11px] text-gray-500 text-center leading-snug">
                      Uses the same random weights as the student app. Full-size wheel still shows confetti for
                      students.
                    </p>
                  )}
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-4 pt-2 border-t border-amber-200/60">
              <button
                onClick={handleSaveConfig}
                disabled={isSavingConfig}
                className="self-start flex items-center gap-2 px-4 py-2.5 bg-amber-600 text-white rounded-xl font-bold text-sm hover:bg-amber-700 disabled:opacity-60"
              >
                {isSavingConfig ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Save Settings
              </button>
            </div>
          </div>
        )}

        <h3 className="font-bold text-gray-800 mb-4">Character Bank ({words.length})</h3>
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
          </div>
        ) : words.length === 0 ? (
          <div className="py-12 text-center text-gray-500 bg-white rounded-xl border-2 border-dashed border-gray-100">
            <p className="mb-2">No characters yet.</p>
            <p className="text-sm">Add characters for the student to practice.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {words.map((w) => (
              <div
                key={w.id}
                className="p-4 bg-white rounded-xl border border-gray-100 flex flex-col items-center gap-2 group"
              >
                <span className="text-3xl font-serif" style={{ fontFamily: 'KaTi Maobi, KaiTi, serif' }}>
                  {w.character}
                </span>
                {w.pinyin && <span className="text-xs text-gray-500">{w.pinyin}</span>}
                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={() => {
                      setEditingWord(w);
                      setNewWord({
                        character: w.character,
                        pinyin: w.pinyin || '',
                        notes: w.notes || '',
                        exampleLinesText: serializeExampleSentencesForTextarea(
                          w.exampleSentences?.length
                            ? w.exampleSentences
                            : w.exampleSentence
                              ? [{ zh: w.exampleSentence }]
                              : []
                        ),
                      });
                    }}
                    className="p-1.5 text-amber-600 hover:bg-amber-50 rounded-lg"
                    title="Edit"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setDeleteConfirm({ isOpen: true, wordId: w.id })}
                    className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg"
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {(showAddModal || editingWord) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
            <h3 className="font-bold text-lg mb-4">{editingWord ? 'Edit Character' : 'Add Character'}</h3>
            <div className="space-y-4">
              <div>
                <label className="text-xs font-bold text-gray-500 block mb-1">Character</label>
                <input
                  type="text"
                  value={newWord.character}
                  onChange={(e) => setNewWord((w) => ({ ...w, character: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-200 rounded-xl"
                  maxLength={4}
                  placeholder="字"
                />
              </div>
              <div>
                <label className="text-xs font-bold text-gray-500 block mb-1">Pinyin (optional)</label>
                <input
                  type="text"
                  value={newWord.pinyin}
                  onChange={(e) => setNewWord((w) => ({ ...w, pinyin: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-200 rounded-xl"
                  placeholder="zhōng"
                />
              </div>
              <div>
                <label className="text-xs font-bold text-gray-500 block mb-1">Notes (optional)</label>
                <input
                  type="text"
                  value={newWord.notes}
                  onChange={(e) => setNewWord((w) => ({ ...w, notes: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-200 rounded-xl"
                  placeholder="Meaning, stroke count, etc."
                />
              </div>
              <div>
                <label className="text-xs font-bold text-gray-500 block mb-1">
                  Example sentences (optional)
                </label>
                <textarea
                  value={newWord.exampleLinesText}
                  onChange={(e) => setNewWord((w) => ({ ...w, exampleLinesText: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-200 rounded-xl min-h-[80px]"
                  placeholder={
                    'One per line. Optional English after pipe:\n小明去超市买菜 | Xiao Ming went to the market to buy food.'
                  }
                  rows={3}
                />
                <p className="text-xs text-gray-400 mt-1">
                  Use <code className="text-[11px] bg-gray-100 px-1 rounded">中文 | English</code> (space-pipe-space)
                  for translation in the quiz. Chinese-only lines are fine.
                </p>
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setEditingWord(null);
                  setNewWord({ character: '', pinyin: '', notes: '', exampleLinesText: '' });
                }}
                className="flex-1 py-2 border border-gray-200 rounded-xl font-bold"
              >
                Cancel
              </button>
              <button
                onClick={editingWord ? handleUpdateWord : handleAddWord}
                disabled={!newWord.character.trim()}
                className="flex-1 py-2 bg-amber-600 text-white rounded-xl font-bold disabled:opacity-50"
              >
                {editingWord ? 'Save' : 'Add'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Batch Modal */}
      {showBatchModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
            <h3 className="font-bold text-lg mb-4">Batch Add Characters</h3>
            <p className="text-sm text-gray-500 mb-4">
              Enter characters separated by spaces, commas, or line breaks.
            </p>
            <textarea
              value={batchInput}
              onChange={(e) => setBatchInput(e.target.value)}
              className="w-full h-32 px-3 py-2 border border-gray-200 rounded-xl font-serif text-lg"
              placeholder="中 国 人 学 汉 字"
            />
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setShowBatchModal(false);
                  setBatchInput('');
                }}
                className="flex-1 py-2 border border-gray-200 rounded-xl font-bold"
              >
                Cancel
              </button>
              <button
                onClick={handleBatchAdd}
                disabled={!batchInput.trim() || isBatchAdding}
                className="flex-1 py-2 bg-amber-600 text-white rounded-xl font-bold disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {isBatchAdding ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                Add
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmationModal
        isOpen={deleteConfirm.isOpen}
        onClose={() => setDeleteConfirm({ isOpen: false, wordId: null })}
        onConfirm={handleDelete}
        title="Delete character?"
        message="This character will be removed from the practice list."
        confirmText="Delete"
        cancelText="Cancel"
        type="warning"
        isLoading={isDeleting}
      />
    </AnimatedCard>
  );
};

export default HanziHeroConfig;
